import { Controller } from "@hotwired/stimulus"
import { createTurboDataTable, destroyTurboDataTable } from "lib/datatables_turbo"
import { show_error, showBlock, dataTruncate } from "lib/helpers"

const NS = ".botsIndex"

// chat/bots/index — data-controller="chat--bots--index"
// Assistants list page: DataTable, type tabs, bulk actions, create modals, interactive guide.
export default class extends Controller {
  static targets = ["table", "addButton"]

  static values = {
    assistantsPath: String,
    usersEditPath: String,
    deleteBotPath: String,
    isAccountBlocked: { type: Boolean, default: false },
    interactiveGuideStatePath: String,
    updateInteractiveGuidePath: String,
    advancedBotEnabled: { type: Boolean, default: false },
  }

  connect() {
    this.botsTable = null
    this.currentFilter = "simple"
    this.totalRecords = undefined
    this.pendingWelcomeCheck = null
    this.createFormCaptureHandler = null

    this.#initDataTable()
    this.#initCheckboxes()
    this.#initCopyHandler()
    this.#initLimitAlertHandler()
    this.#initCreateModalHandlers()
    window.addEventListener("resize", this.boundResize)
    this.#initGuide()
  }

  disconnect() {
    window.removeEventListener("resize", this.boundResize)

    if (this.botsTable) {
      this.botsTable = destroyTurboDataTable(this.botsTable)
    } else if ($.fn.DataTable.isDataTable("#bots_list")) {
      destroyTurboDataTable($("#bots_list").DataTable())
    }

    if (this.createFormCaptureHandler) {
      document.removeEventListener("click", this.createFormCaptureHandler, true)
      this.createFormCaptureHandler = null
    }

    $(document).off(NS)
    $(document).off("click.chooseGuide")
    $("#check-all-bots").off(NS)
    $("input#check-all-bots").off(NS)
    $(".copy-bot-id").off("click")

    $("#choose-assistant-modal").off("hidden.bs.modal" + NS).off("hidden.bs.modal.chooseGuide")
    $("#create-assistant-form").off("submit.create-assistant").removeData("ajaxForm-initialized")
    $("#create-assistant-modal").off("shown.bs.modal" + NS).off("hidden.bs.modal" + NS)
    $("#welcome-modal-close-btn").off("click" + NS)
    $("#welcome-modal-get-started-btn").off("click" + NS)
    $("#welcome-assistant-modal").off("hidden.bs.modal" + NS)
    $("#guide-restart-button").off("click" + NS).removeData("click-bound")

    $("#guide-completion-popup").remove()
    $(".driver-overlay, .driver-popover, .driverjs-popover").remove()
    document.body.classList.remove("choose-type-guide-active")

    this.totalRecords = undefined
    this.pendingWelcomeCheck = null
    this.currentFilter = "simple"
  }

  // ── Stimulus actions ──────────────────────────────────────────────────────

  removeBots(event) {
    if (event) event.preventDefault()

    const groupName = $("input#check-all-bots").val()
    const checkboxes = $(`input[name^='${groupName}']:checked`)
    const available = $(`input[name^='${groupName}']`)

    if (!available.length) return
    if (!checkboxes.length) {
      alert("Please select at least one item")
      return
    }
    if (!confirm("Are you sure? All selected items will be removed")) return

    $(".dataTables_processing").css("display", "block")

    const ids = $.makeArray(checkboxes.map(function () { return $(this).val() }))

    fetch(this.deleteBotPathValue, {
      method: "DELETE",
      headers: {
        "Content-Type": "application/json",
        "X-CSRF-Token": this.#csrfToken,
        "X-Requested-With": "XMLHttpRequest",
      },
      body: JSON.stringify({ item_ids: ids }),
    })
      .then((response) => {
        if (!response.ok) throw new Error("Network response was not ok")
        return response.json()
      })
      .then(() => { this.botsTable.ajax.reload() })
      .catch(() => { show_error("An error occurred during request") })

    $("#check-all-bots").prop("checked", false)
  }

  filterTab(event) {
    event.preventDefault()
    const selectedType = event.currentTarget.dataset.assistantType
    if (selectedType === this.currentFilter) return
    this.currentFilter = selectedType
    this.botsTable.ajax.reload()
  }

  // ── Class-field handlers ──────────────────────────────────────────────────

  boundResize = () => {
    if (!this.botsTable) return
    this.botsTable = destroyTurboDataTable(this.botsTable, { clear: true })
    this.#initDataTable()
    this.#initCheckboxes()
    this.#initCopyHandler()
    this.#initLimitAlertHandler()
  }

  // ── Helpers ───────────────────────────────────────────────────────────────

  get #csrfToken() {
    return document.querySelector('meta[name="csrf-token"]')?.content
  }

  #findCreateButton() {
    return (
      document.getElementById("create-assistant-button-header") ||
      this.element.querySelector(".head-toolbar .open-create-assistant-modal") ||
      this.element.querySelector(".head-toolbar a.new-btn") ||
      this.element.querySelector(".open-create-assistant-modal") ||
      document.getElementById("add-bot-button")
    )
  }

  // ── DataTable ─────────────────────────────────────────────────────────────

  #initDataTable() {
    const assistantsPath = this.assistantsPathValue
    const usersEditPath = this.usersEditPathValue

    this.botsTable = createTurboDataTable(
      this.hasTableTarget ? this.tableTarget : "#bots_list",
      {
        dom: '<"head-toolbar">rt<"footer"li<"footer-toolbar">p>',
        scrollX: true,
        pagingType: "full_numbers",
        processing: true,
        serverSide: true,
        autoWidth: false,
        bSort: false,
        order: [4, "asc"],
        responsive: true,
        language: { emptyTable: "" },
        ajax: {
          url: assistantsPath,
          data: (d) => { d.assistant_type = this.currentFilter },
          dataSrc: (json) => {
            if (json?.recordsTotal !== undefined) {
              this.totalRecords = json.recordsTotal
              if (this.pendingWelcomeCheck) {
                this.pendingWelcomeCheck()
                this.pendingWelcomeCheck = null
              }
            }
            return json.data || []
          },
        },
        footerCallback: () => { $("#check-all-bots").attr("checked", false) },
        drawCallback: () => { if (this.botsTable) this.#onDraw() },
        columnDefs: this.#columnDefs(assistantsPath, usersEditPath),
      },
    )

    if ($("#reader").val() === "false" && !this.isAccountBlockedValue) {
      $(".footer-toolbar").html(
        '<a href="#" class="remove" data-action="click->chat--bots--index#removeBots">Remove selected</a>',
      )
    }
  }

  #columnDefs(assistantsPath, usersEditPath) {
    return [
      {
        targets: 0,
        orderable: false,
        width: "28px",
        createdCell: (cell, cellData) => {
          $(cell).html(
            `<input type="checkbox" class="table-checkbox-ai-chatbot" id="check--chatbot${cellData}" name="item_ids[]" value="${cellData}"/> <label for="check--chatbot${cellData}"></label>`,
          )
        },
      },
      {
        targets: 1,
        orderable: false,
        createdCell: (cell, cellData, rowData) => {
          const id = rowData[0]
          const nameData = Array.isArray(cellData) ? cellData : [cellData, null]
          const name = nameData[0] || ""
          const avatar = nameData[1] || null
          const initials = name.length >= 2
            ? name.substring(0, 2).toUpperCase()
            : name.toUpperCase()

          let html = '<div class="assistant-name-cell">'
          if (avatar?.trim()) {
            html += `<div class="assistant-avatar-icon"><img src="${avatar}" alt="${name}" class="assistant-avatar-image" /></div>`
          } else {
            html += `<div class="assistant-avatar-icon">${initials}</div>`
          }
          html += `<a href="${assistantsPath}/${id}/edit" class="assistant-name-link">${name}</a></div>`
          $(cell).html(html)
        },
      },
      {
        targets: 2,
        orderable: false,
        width: "230px",
        createdCell: (cell, cellData) => {
          $(cell).html(
            `<span class="copy-text" data-bot-id="${cellData}">${cellData}</span><i class="copy-bot-id bg-copy" data-bot-id="${cellData}"/>`,
          )
        },
      },
      {
        targets: 3,
        createdCell: (cell, cellData) => {
          $(cell).html(`<a href="${usersEditPath}/edit/${cellData}">${dataTruncate(cellData, 15)}</a>`)
        },
      },
      {
        targets: 4,
        createdCell: (cell, cellData) => { $(cell).html(cellData || "") },
      },
      { targets: [0, 1, 2, 3, 4], sortable: false },
    ]
  }

  #onDraw() {
    this.#initCopyHandler()

    const rowCount = this.botsTable.rows({ filter: "applied" }).count()
    const total = this.totalRecords !== undefined ? this.totalRecords : 0
    const isEmpty = rowCount === 0 || total === 0

    $(".head-toolbar").empty()

    let tabsHtml =
      '<div class="assistant-tabs-group">' +
      `<a href="#" class="assistant-tab-button${this.currentFilter === "simple" ? " active" : ""}" data-assistant-type="simple" data-action="click->chat--bots--index#filterTab">Knowledge Agents</a>`
    if (this.advancedBotEnabledValue) {
      tabsHtml +=
        `<a href="#" class="assistant-tab-button${this.currentFilter === "advanced" ? " active" : ""}" data-assistant-type="advanced" data-action="click->chat--bots--index#filterTab">Workflow Agents</a>`
    }
    tabsHtml += "</div>"
    $(".head-toolbar").append(tabsHtml)

    if (isEmpty) {
      setTimeout(() => {
        let emptyRow = $("#bots_list tbody tr.dataTables_empty")
        if (emptyRow.length === 0) {
          const tbody = $("#bots_list tbody")
          if (tbody.children("tr").length === 0) {
            tbody.html('<tr class="dataTables_empty"><td colspan="5"></td></tr>')
            emptyRow = $("#bots_list tbody tr.dataTables_empty")
          } else {
            emptyRow = tbody.find("tr:first-child")
          }
        }
        if (emptyRow.length > 0) {
          const notification =
            '<div class="assistants-empty-state" id="assistants-empty-state-container">' +
            '<p class="assistants-empty-state-message">No AI Agents created.</p></div>'
          const emptyCell = emptyRow.find("td")
          if (emptyCell.length > 0) {
            emptyCell.attr("colspan", "5").html(notification)
          } else {
            emptyRow.html(`<td colspan="5">${notification}</td>`)
          }
        }
      }, 10)
    }

    const sourceButton = this.hasAddButtonTarget ? $(this.addButtonTarget) : $("#add-bot-button")
    const clonedButton = sourceButton.clone().removeClass("hide_button")
    if (!clonedButton.hasClass("block_assistant_creation")) {
      clonedButton
        .attr("href", "#")
        .addClass("open-create-assistant-modal")
        .attr("id", "create-assistant-button-header")
    }
    $(".head-toolbar").append(clonedButton)
  }

  // ── Checkboxes ────────────────────────────────────────────────────────────

  #initCheckboxes() {
    $("#check-all-bots").off("change" + NS).on("change" + NS, function () {
      const isChecked = $(this).prop("checked")
      $(".table-checkbox-ai-chatbot").prop("checked", isChecked)
      $(".table-checkbox-ai-chatbot").closest("tr").toggleClass("selected-row", isChecked)
    })

    $(document).off("change" + NS, ".table-checkbox-ai-chatbot")
    $(document).on("change" + NS, ".table-checkbox-ai-chatbot", function () {
      $(this).closest("tr").toggleClass("selected-row", $(this).prop("checked"))
      const selectedCount = $("input.table-checkbox-ai-chatbot:checked").length
      const totalCount = $("input.table-checkbox-ai-chatbot").length
      $("#check-all-bots").prop("checked", selectedCount > 0 && selectedCount === totalCount)
    })
  }

  // ── Copy bot ID ───────────────────────────────────────────────────────────

  #initCopyHandler() {
    $(".copy-bot-id").off("click").on("click", (e) => {
      e.preventDefault()
      this.#copyBotId(e)
    })
  }

  #copyBotId(event) {
    const botId = $(event.currentTarget).data("bot-id")
    const tempTextArea = document.createElement("textarea")
    tempTextArea.value = botId
    document.body.appendChild(tempTextArea)
    tempTextArea.select()
    const success = document.execCommand("copy")
    document.body.removeChild(tempTextArea)

    const $el = $(event.currentTarget)
    if (success) {
      $el.addClass("copy-success")
      setTimeout(() => $el.removeClass("copy-success"), 2000)
    } else {
      $el.addClass("copy-error")
      setTimeout(() => $el.removeClass("copy-error"), 2000)
      console.error("Failed to copy botID to clipboard")
    }
  }

  // ── Limit alert ───────────────────────────────────────────────────────────

  #dismissDriverOverlay() {
    document.body.classList.remove("driver-active", "driver-fade")
    document.querySelector(".driver-active")?.classList.remove("driver-active")
    document.querySelectorAll(".driver-overlay, .driver-popover, .driverjs-popover").forEach((el) => el.remove())
  }

  #creationIsBlocked(element) {
    const sourceButton = this.hasAddButtonTarget ? this.addButtonTarget : document.getElementById("add-bot-button")
    return Boolean(
      element?.classList?.contains("block_assistant_creation") ||
      sourceButton?.classList?.contains("block_assistant_creation"),
    )
  }

  #showAssistantsLimitModal() {
    this.#dismissDriverOverlay()
    const modal = $("#blocked_assistants_creation")
    modal.css("z-index", "1100000000")
    modal.modal("show")
    setTimeout(() => { $(".modal-backdrop").css("z-index", "1099999999") }, 10)
  }

  #initLimitAlertHandler() {
    $(document).off("click" + NS, ".block_assistant_creation")
    $(document).on("click" + NS, ".block_assistant_creation", (e) => {
      e.preventDefault()
      this.#showAssistantsLimitModal()
    })
  }

  // ── Create modal ──────────────────────────────────────────────────────────

  #initCreateModalHandlers() {
    $(document).off("click" + NS, ".choose-assistant-type-card")
    $(document).on("click" + NS, ".choose-assistant-type-card", function () {
      $(".choose-assistant-type-card").removeClass("active")
      $(this).addClass("active")
      $("#chosen-assistant-type").val($(this).data("type"))
    })

    $(document).off("click" + NS, "#choose-assistant-modal-button")
    $(document).on("click" + NS, "#choose-assistant-modal-button", () => {
      const selectedType = $("#chosen-assistant-type").val()
      $("#create-assistant-type").val(selectedType)
      $("#choose-assistant-modal").modal("hide")

      const createModal = $("#create-assistant-modal")
      createModal.css("z-index", "1100000000")
      createModal.modal("show")
      setTimeout(() => { $(".modal-backdrop").css("z-index", "1099999999") }, 10)
    })

    $("#choose-assistant-modal").off("hidden.bs.modal" + NS).on("hidden.bs.modal" + NS, () => {
      $(".choose-assistant-type-card").removeClass("active")
      $('.choose-assistant-type-card[data-type="simple"]').addClass("active")
      $("#chosen-assistant-type").val("simple")
    })

    $(document).off("click" + NS, ".open-create-assistant-modal")
    $(document).on("click" + NS, ".open-create-assistant-modal", (e) => {
      e.preventDefault()
      this.#openCreateOrChooseModal()
    })

    $(document).off("input keyup" + NS, "#create-assistant-name-input")
    $(document).on("input keyup" + NS, "#create-assistant-name-input", function () {
      const nameValue = $(this).val().trim()
      $("#create-assistant-modal-button").prop("disabled", nameValue.length < 1)
    })

    if (this.createFormCaptureHandler) {
      document.removeEventListener("click", this.createFormCaptureHandler, true)
    }
    this.createFormCaptureHandler = (e) => {
      if (e.target.id === "create-assistant-modal-button") {
        e.preventDefault()
        e.stopPropagation()
        e.stopImmediatePropagation()
        this.#handleCreateSubmit(e.target)
        return false
      }
    }
    document.addEventListener("click", this.createFormCaptureHandler, true)

    this.#initCreateFormHandler()

    $("#create-assistant-modal").off("shown.bs.modal" + NS).on("shown.bs.modal" + NS, () => {
      const nameValue = $("#create-assistant-name-input").val().trim()
      if (nameValue.length < 1) {
        $("#create-assistant-modal-button").prop("disabled", true)
      }
      this.#initCreateFormHandler()
    })

    $("#create-assistant-modal").off("hidden.bs.modal" + NS).on("hidden.bs.modal" + NS, () => {
      $("#create-assistant-form")[0].reset()
      $("#create-assistant-modal-errors").empty().hide()
      $("#create-assistant-modal-button").prop("disabled", true)
    })
  }

  #openCreateOrChooseModal() {
    this.#dismissDriverOverlay()

    if (this.#creationIsBlocked()) {
      this.#showAssistantsLimitModal()
      return
    }

    if (!this.advancedBotEnabledValue) {
      $("#create-assistant-type").val("simple")
      const createModal = $("#create-assistant-modal")
      createModal.css("z-index", "1100000000")
      createModal.modal("show")
      setTimeout(() => { $(".modal-backdrop").css("z-index", "1099999999") }, 10)
      return
    }

    const modal = $("#choose-assistant-modal")
    modal.css("z-index", "1100000000")
    modal.modal("show")
    setTimeout(() => { $(".modal-backdrop").css("z-index", "1099999999") }, 10)
  }

  #initCreateFormHandler() {
    const form = $("#create-assistant-form")
    if (form.length === 0 || form.data("ajaxForm-initialized")) return

    form.off("submit.create-assistant").on("submit.create-assistant", (e) => {
      e.preventDefault()
      e.stopPropagation()
      this.#handleCreateSubmit(document.getElementById("create-assistant-modal-button"))
      return false
    })
    form.data("ajaxForm-initialized", true)
  }

  #handleCreateSubmit(button) {
    const $button = $(button)
    if ($button.prop("disabled")) return

    const form = $("#create-assistant-form")
    if (form.length === 0) return

    const nameValue = $("#create-assistant-name-input").val().trim()
    if (nameValue.length < 1) return

    const formData = new FormData(form[0])
    $("#create-assistant-modal-errors").empty().hide()
    $button.prop("disabled", true)

    fetch(form.attr("action"), {
      method: "POST",
      headers: {
        "X-CSRF-Token": this.#csrfToken,
        "X-Requested-With": "XMLHttpRequest",
      },
      body: formData,
    })
      .then((response) => {
        if (!response.ok) return response.json().then((data) => { throw { responseJSON: data } })
        return response.json()
      })
      .then((response) => {
        if (response.success) {
          $("#create-assistant-modal").modal("hide")
          form[0].reset()
          if (this.botsTable) this.botsTable.ajax.reload()
          if (response.path) location.href = response.path
        } else {
          $("#create-assistant-modal").modal("hide")
          const msg = response.message || response.messages?.[0] || "An error occurred"
          showBlock("#uploading-errors", msg)
          $button.prop("disabled", false)
        }
      })
      .catch((error) => {
        console.error("Request error:", error)
        $("#create-assistant-modal").modal("hide")
        let msg = "An error occurred during request"
        if (error.responseJSON?.message) msg = error.responseJSON.message
        else if (error.responseJSON?.messages?.[0]) msg = error.responseJSON.messages[0]
        showBlock("#uploading-errors", msg)
        $button.prop("disabled", false)
      })
  }

  // ── Interactive guide ─────────────────────────────────────────────────────

  #guideHeaders() {
    return {
      "Content-Type": "application/json",
      "X-CSRF-Token": this.#csrfToken,
      "X-Requested-With": "XMLHttpRequest",
      "Accept": "application/json",
    }
  }

  #fetchGuideState() {
    if (!this.interactiveGuideStatePathValue) {
      return Promise.reject(new Error("Guide state path not available"))
    }
    return fetch(this.interactiveGuideStatePathValue, {
      method: "GET",
      headers: {
        "X-CSRF-Token": this.#csrfToken,
        "X-Requested-With": "XMLHttpRequest",
        "Accept": "application/json",
      },
    }).then((r) => {
      if (!r.ok) throw new Error("Network response was not ok")
      return r.json()
    })
  }

  #updateGuideStep(guideType, step, completed) {
    if (!this.updateInteractiveGuidePathValue) {
      return Promise.reject(new Error("Update guide path not available"))
    }
    const payload = { guide_type: guideType, step }
    if (completed !== undefined) payload.completed = completed
    return fetch(this.updateInteractiveGuidePathValue, {
      method: "POST",
      headers: this.#guideHeaders(),
      body: JSON.stringify(payload),
    })
  }

  #skipAllGuides() {
    if (!this.updateInteractiveGuidePathValue) {
      return Promise.reject(new Error("Update guide path not available"))
    }
    return fetch(this.updateInteractiveGuidePathValue, {
      method: "POST",
      headers: this.#guideHeaders(),
      body: JSON.stringify({ guide_type: "skip_all" }),
    })
  }

  #resetAllGuides() {
    if (!this.updateInteractiveGuidePathValue) {
      return Promise.reject(new Error("Update guide path not available"))
    }
    return fetch(this.updateInteractiveGuidePathValue, {
      method: "POST",
      headers: this.#guideHeaders(),
      body: JSON.stringify({ guide_type: "reset_all" }),
    })
  }

  #getDriverFn() {
    const driverFn = window.driver?.js?.driver
    if (!driverFn || typeof driverFn !== "function") {
      console.error("Driver.js is not loaded properly")
      return null
    }
    return driverFn
  }

  #initGuide() {
    this.#initGuideRestartButton()

    this.#fetchGuideState()
      .then((state) => {
        if (state.guide_skipped) return

        let hasAssistants = state.has_assistants || false
        if (state.has_assistants === undefined) {
          if (this.totalRecords !== undefined) {
            hasAssistants = this.totalRecords > 0
          } else if (this.botsTable) {
            try {
              hasAssistants = this.botsTable.rows({ filter: "applied" }).count() > 0
            } catch (_e) {
              hasAssistants = $("#bots_list tbody tr:not(.dataTables_empty)").length > 0
            }
          } else {
            hasAssistants = $("#bots_list tbody tr:not(.dataTables_empty)").length > 0
          }
        }

        const shouldShowWelcome =
          state.first_assistant_creation_last_step === 0 &&
          !state.first_assistant_creation_completed &&
          !state.first_assistant_creation_skipped &&
          !hasAssistants

        if (shouldShowWelcome) {
          setTimeout(() => {
            const modal = $("#welcome-assistant-modal")
            if (modal.length > 0) modal.modal("show")
            else console.error("Welcome modal element not found in DOM")
          }, 500)
        } else if (
          state.first_assistant_creation_last_step === 1 &&
          !state.first_assistant_creation_completed &&
          !state.first_assistant_creation_skipped &&
          !hasAssistants
        ) {
          setTimeout(() => this.#startFirstAssistantStep2(), 500)
        } else if (
          hasAssistants &&
          !state.new_assistant_creation_completed &&
          !state.new_assistant_creation_skipped
        ) {
          setTimeout(() => this.#startNewAssistantGuide(), 500)
        }
      })
      .catch((error) => console.error("Failed to load interactive guide state", error))

    $("#welcome-modal-close-btn").off("click" + NS).on("click" + NS, (e) => {
      e.preventDefault()
      this.#skipAllGuides().catch((err) => console.error("Failed to skip guide", err))
      $("#welcome-assistant-modal").modal("hide")
    })

    $("#welcome-modal-get-started-btn").off("click" + NS).on("click" + NS, () => {
      const button = $("#welcome-modal-get-started-btn")
      button.prop("disabled", true)

      this.#updateGuideStep("first_assistant_creation", 1)
        .then((r) => r.json())
        .then((data) => {
          if (data.success) {
            $("#welcome-assistant-modal").modal("hide")
            $("#welcome-assistant-modal")
              .off("hidden.bs.modal" + NS)
              .on("hidden.bs.modal" + NS, () => {
                $("#welcome-assistant-modal").off("hidden.bs.modal" + NS)
                setTimeout(() => this.#startFirstAssistantStep2(), 100)
              })
          } else {
            console.error("Failed to update interactive guide state")
            button.prop("disabled", false)
          }
        })
        .catch((error) => {
          console.error("Failed to update interactive guide state", error)
          button.prop("disabled", false)
        })
    })
  }

  #startFirstAssistantStep2() {
    const btn = this.#findCreateButton()
    if (!btn) {
      setTimeout(() => {
        const retry = this.#findCreateButton()
        if (retry) this.#startGuideWithButton(retry, "first_assistant_creation", 2)
        else console.error("Create button not found for first assistant guide")
      }, 1000)
      return
    }
    this.#startGuideWithButton(btn, "first_assistant_creation", 2)
  }

  #startNewAssistantGuide() {
    const btn = this.#findCreateButton()
    if (!btn) {
      setTimeout(() => {
        const retry = this.#findCreateButton()
        if (!retry) { console.error("Create button not found for new assistant guide"); return }
        this.#startGuideWithButton(retry, "new_assistant_creation", 1)
      }, 300)
      return
    }
    this.#startGuideWithButton(btn, "new_assistant_creation", 1)
  }

  #fixPopoverPosition(selector) {
    const el = document.querySelector(selector || ".driver-popover")
    if (!el) return

    const rect = el.getBoundingClientRect()
    const vw = window.innerWidth
    const min = 45

    if (rect.left < min) {
      el.style.left = min + "px"
      el.style.transform = "translateY(-50%)"
    }
    if (rect.right > vw - min) {
      el.style.left = (vw - rect.width - min) + "px"
    }
  }

  #startGuideWithButton(targetElement, guideType = "new_assistant_creation", guideStep = 1) {
    const driverFn = this.#getDriverFn()
    if (!driverFn) return

    let guideCompleted = false
    let driverObj

    const markCompleted = () => {
      if (guideCompleted) return
      guideCompleted = true
      this.#updateGuideStep(guideType, guideStep, true)
        .catch((err) => console.error("Failed to mark guide as completed", err))
    }

    const handleButtonClick = () => {
      markCompleted()
      if (driverObj) driverObj.destroy()

      if (this.#creationIsBlocked(targetElement)) {
        this.#showAssistantsLimitModal()
        return
      }

      if (this.advancedBotEnabledValue) {
        const chooseModal = $("#choose-assistant-modal")
        chooseModal.css("z-index", "1100000000")
        chooseModal.one("shown.bs.modal", () => this.#startChooseTypeGuide())
        chooseModal.modal("show")
      } else {
        $("#create-assistant-type").val("simple")
        const createModal = $("#create-assistant-modal")
        createModal.css("z-index", "1100000000")
        createModal.modal("show")
      }
      setTimeout(() => { $(".modal-backdrop").css("z-index", "1099999999") }, 10)
    }

    const documentClickHandler = (e) => {
      let isButtonClick = targetElement.contains(e.target) || e.target === targetElement

      if (!isButtonClick) {
        const rect = targetElement.getBoundingClientRect()
        isButtonClick =
          e.clientX >= rect.left && e.clientX <= rect.right &&
          e.clientY >= rect.top && e.clientY <= rect.bottom
      }

      if (isButtonClick) {
        e.stopPropagation()
        e.stopImmediatePropagation()
        e.preventDefault()
        handleButtonClick()
      }
    }

    document.addEventListener("click", documentClickHandler, true)

    driverObj = driverFn({
      showProgress: false,
      showButtons: [],
      allowClose: false,
      overlayColor: "rgba(0, 0, 0, 0.6)",
      stagePadding: 8,
      stageRadius: 8,
      popoverOffset: 15,
      popoverClass: "new-assistant-guide-popover",
      steps: [{
        element: targetElement,
        popover: {
          title: "",
          description:
            '<div class="guide-popover-content">' +
            '<p>Let\'s create your AI Agent by clicking "Create"</p>' +
            '<a href="#" class="guide-skip-link">Skip tour</a></div>',
          side: "bottom",
          align: "end",
        },
      }],
      onPopoverRender: () => {
        const popover = document.querySelector(".new-assistant-guide-popover")
        if (!popover) return

        this.#fixPopoverPosition(".new-assistant-guide-popover")

        const nav = popover.querySelector(".driver-popover-navigation")
        if (nav) nav.style.display = "none"
        const footer = popover.querySelector(".driver-popover-footer, .driver-popover-navigation-btns")
        if (footer) footer.style.display = "none"

        setTimeout(() => {
          const skipLink = popover.querySelector(".guide-skip-link")
          if (skipLink) {
            skipLink.addEventListener("click", (e) => {
              e.preventDefault()
              e.stopPropagation()
              this.#skipAllGuides().then(() => {
                if (driverObj) driverObj.destroy()
                setTimeout(() => {
                  this.#initGuideRestartButton()
                  this.#showGuideCompletionPopup()
                }, 300)
              })
            })
          }
        }, 10)
      },
      onDestroyed: () => {
        document.removeEventListener("click", documentClickHandler, true)
      },
    })

    driverObj.drive()
  }

  #startChooseTypeGuide() {
    const driverFn = this.#getDriverFn()
    if (!driverFn) return

    const targetElement = document.querySelector("#choose-assistant-modal .choose-assistant-type-cards")
    if (!targetElement) return

    document.body.classList.add("choose-type-guide-active")
    let driverObj
    let tornDown = false

    const teardown = () => {
      if (tornDown) return
      tornDown = true
      document.body.classList.remove("choose-type-guide-active")
      $("#choose-assistant-modal").off("hidden.bs.modal.chooseGuide")
      $(document).off("click.chooseGuide")
      if (driverObj) driverObj.destroy()
    }

    $("#choose-assistant-modal").one("hidden.bs.modal.chooseGuide", teardown)
    $(document).on(
      "click.chooseGuide",
      ".choose-assistant-type-card, #choose-assistant-modal-button",
      () => setTimeout(teardown, 0),
    )

    driverObj = driverFn({
      showProgress: false,
      showButtons: [],
      allowClose: false,
      overlayOpacity: 0.5,
      stagePadding: 8,
      stageRadius: 8,
      popoverOffset: 15,
      popoverClass: "choose-type-guide-popover",
      steps: [{
        element: targetElement,
        popover: {
          title: "",
          description:
            '<div class="guide-popover-content">' +
            '<p>Pick a type for your AI Agent</p>' +
            '<a href="#" class="guide-skip-link">Skip tour</a></div>',
          side: "bottom",
          align: "center",
        },
      }],
      onPopoverRender: () => {
        const popover = document.querySelector(".choose-type-guide-popover")
        if (!popover) return

        this.#fixPopoverPosition(".choose-type-guide-popover")

        const nav = popover.querySelector(".driver-popover-navigation")
        if (nav) nav.style.display = "none"
        const footer = popover.querySelector(".driver-popover-footer, .driver-popover-navigation-btns")
        if (footer) footer.style.display = "none"

        setTimeout(() => {
          const skipLink = popover.querySelector(".guide-skip-link")
          if (skipLink) {
            skipLink.addEventListener("click", (e) => {
              e.preventDefault()
              e.stopPropagation()
              this.#skipAllGuides().then(() => {
                $("#choose-assistant-modal").modal("hide")
                teardown()
                setTimeout(() => {
                  this.#initGuideRestartButton()
                  this.#showGuideCompletionPopup()
                }, 300)
              })
            })
          }
        }, 10)
      },
    })

    driverObj.drive()
  }

  #initGuideRestartButton() {
    const button = $("#guide-restart-button")
    if (button.length === 0) return

    if (!button.data("click-bound")) {
      button.off("click" + NS).on("click" + NS, (e) => {
        e.preventDefault()
        e.stopPropagation()
        this.#restartGuide()
      })
      button.data("click-bound", true)
    }

    const urlParams = new URLSearchParams(window.location.search)
    if (urlParams.get("guide_completed") === "true") {
      window.history.replaceState({}, document.title, window.location.pathname)
      this.#showGuideCompletionPopup()
    }
  }

  #showGuideCompletionPopup() {
    $("#guide-completion-popup").remove()
    $(".driver-overlay, .driver-popover, .driverjs-popover").remove()

    const restartButton = document.getElementById("guide-restart-button")
    if (!restartButton) {
      this.#initGuideRestartButton()
      if (!document.getElementById("guide-restart-button")) {
        console.error("Restart button not found")
        return
      }
    }

    $("body").append(
      '<div id="guide-completion-popup" class="guide-completion-popup">' +
      '<div class="guide-completion-content">' +
      "<h4>You're all set!</h4>" +
      '<p>You can revisit this guide anytime by pressing the question mark icon</p>' +
      '<button class="guide-completion-ok-btn">Ok</button>' +
      "</div></div>",
    )

    const driverFn = this.#getDriverFn()
    let completionDriver = null

    if (driverFn && restartButton) {
      completionDriver = driverFn({
        showProgress: false,
        showButtons: [],
        allowClose: false,
        overlayOpacity: 0.5,
        stagePadding: 4,
        stageRadius: 4,
        allowKeyboardControl: false,
        smoothScroll: false,
        disableActiveInteraction: false,
        popoverOffset: 15,
        steps: [{ element: restartButton, popover: null }],
        onHighlightStarted: () => {
          setTimeout(() => {
            const overlay = document.querySelector(".driver-overlay")
            if (overlay) overlay.style.pointerEvents = "none"
            const p = document.querySelector(".driver-popover, .driverjs-popover")
            if (p) p.style.display = "none"
          }, 50)
        },
        onHighlighted: () => {
          const overlay = document.querySelector(".driver-overlay")
          if (overlay) overlay.style.pointerEvents = "none"
          const p = document.querySelector(".driver-popover, .driverjs-popover")
          if (p) p.style.display = "none"
        },
        onDestroyed: () => {
          $("#guide-completion-popup").fadeOut(200, function () { $(this).remove() })
        },
      })
      completionDriver.drive()
    }

    $(document).off("click" + NS, "#guide-completion-popup .guide-completion-ok-btn")
    $(document).on("click" + NS, "#guide-completion-popup .guide-completion-ok-btn", (e) => {
      e.preventDefault()
      e.stopPropagation()
      if (completionDriver) {
        completionDriver.destroy()
      } else {
        $("#guide-completion-popup").fadeOut(200, function () { $(this).remove() })
      }
    })
  }

  #restartGuide() {
    this.#resetAllGuides()
      .then(() => window.location.reload())
      .catch((error) => console.error("Failed to restart guide", error))
  }
}
